import { useState, useEffect, useCallback } from "react";
import { fetchProducts } from "../api";
import { CISA_SECTORS, SECTOR_EMOJI } from "../utils/sectors";
import ProductsByCnaPanel from "./ProductsByCnaPanel";

const PER_PAGE = 50;

function fmtTime(iso) {
  if (!iso) return "--";
  return new Date(iso).toLocaleString([], {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function ProductsPanel() {
  const [data, setData] = useState({ products: [], meta: { total: 0, scored: 0 }, filtered: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [scoredFilter, setScoredFilter] = useState("");
  const [tab, setTab] = useState("list");

  const load = useCallback(() => {
    setLoading(true);
    fetchProducts({ page, perPage: PER_PAGE, search, scored: scoredFilter })
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [page, search, scoredFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    setSearch(searchInput.trim());
  };

  const handleFilterChange = (e) => {
    setScoredFilter(e.target.value);
    setPage(0);
  };

  const { products, meta, filtered, total_pages: totalPages } = data;

  return (
    <div className="products-page">
      <div className="table-tabs">
        {[
          { key: "list", label: "All Products" },
          { key: "cna", label: "By CNA" },
        ].map(({ key, label }) => (
          <button
            key={key}
            className={`table-tab ${tab === key ? "active" : ""}`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Mounted only while selected: the per-CNA rollup is a separate,
          much more expensive query, and most visits never open it. */}
      {tab === "cna" && <ProductsByCnaPanel />}

      <div className="panel" style={{ display: tab === "list" ? undefined : "none" }}>
        <div className="panel-header">
          Products
          <span className="products-summary">
            {meta.total.toLocaleString()} products &middot; {meta.scored.toLocaleString()} scored
          </span>
        </div>

        <form className="products-toolbar" onSubmit={handleSearch}>
          <input
            className="products-search-input"
            type="text"
            placeholder="Search vendor or product..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <select className="products-filter-select" value={scoredFilter} onChange={handleFilterChange}>
            <option value="">All products</option>
            <option value="true">Scored only</option>
            <option value="false">Unscored only</option>
          </select>
          <button type="submit" className="flush-btn">Search</button>
          {(search || scoredFilter) && (
            <button
              type="button"
              className="flush-btn"
              onClick={() => { setSearchInput(""); setSearch(""); setScoredFilter(""); setPage(0); }}
            >
              Clear
            </button>
          )}
        </form>

        {loading ? (
          <div className="loading">Loading products...</div>
        ) : products.length === 0 ? (
          <div className="jobs-empty">No products match this filter.</div>
        ) : (
          <>
            <table className="products-table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Product</th>
                  <th>Global Prevalence</th>
                  <th>Sectors</th>
                  <th>Confidence</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => {
                  const prevalence = p.prevalence || {};
                  const level = prevalence.global;
                  const sectors = prevalence.sectors || {};
                  const sectorEntries = Object.entries(sectors);
                  const showCanonical =
                    (p.canonical_vendor && p.canonical_vendor !== p.vendor) ||
                    (p.canonical_product && p.canonical_product !== p.product);

                  return (
                    <tr key={`${p.vendor}::${p.product}`} className="products-row">
                      <td className="products-vendor">
                        {/* A product whose CNA gave only a placeholder vendor
                            is stored with an empty one; a bare blank cell
                            reads as a rendering fault rather than a fact
                            about the record. */}
                        {p.vendor || <span className="products-novendor">not stated</span>}
                        {showCanonical && p.canonical_vendor && p.canonical_vendor !== p.vendor && (
                          <div className="products-canonical">→ {p.canonical_vendor}</div>
                        )}
                      </td>
                      <td className="products-product">
                        {p.product}
                        {showCanonical && p.canonical_product && p.canonical_product !== p.product && (
                          <div className="products-canonical">→ {p.canonical_product}</div>
                        )}
                      </td>
                      <td>
                        {level ? (
                          <span
                            className={`products-badge products-badge-${level} products-tooltip`}
                            data-tooltip={prevalence.rationale || "No rationale recorded"}
                          >
                            {level}
                          </span>
                        ) : (
                          <span className="jobs-muted">not yet scored</span>
                        )}
                      </td>
                      <td className="products-sectors">
                        {sectorEntries.length === 0 ? (
                          <span className="jobs-muted">--</span>
                        ) : (
                          sectorEntries.map(([slug, entry]) => {
                            // Backward-compat: older scored rows store a bare
                            // level string; newer ones store {level, rationale}.
                            const isObj = entry && typeof entry === "object";
                            const lvl = isObj ? entry.level : entry;
                            const sectorName = CISA_SECTORS[slug] || slug;
                            const tooltip = isObj && entry.rationale
                              ? `${sectorName}: ${entry.rationale}`
                              : sectorName;
                            return (
                              <span
                                key={slug}
                                className={`products-badge products-badge-sm products-badge-${lvl} products-tooltip`}
                                data-tooltip={tooltip}
                              >
                                <span aria-hidden="true">{SECTOR_EMOJI[slug]} </span>
                                {sectorName.split(" ")[0]}
                              </span>
                            );
                          })
                        )}
                      </td>
                      <td>{prevalence.confidence || <span className="jobs-muted">--</span>}</td>
                      <td className="jobs-time">{fmtTime(p.updated_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="pagination">
              <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>&larr; Prev</button>
              <span className="pagination-info">
                Page {page + 1} of {totalPages} &middot; {filtered.toLocaleString()} matching
              </span>
              <button disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next &rarr;</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
